import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, Circle, FileText, FolderOpen, Lock, PlayCircle } from "lucide-react";

const getContentStatusMeta = (content) => {
    if (content.locked) return { icon: Lock, label: "Verrouille", className: "is-locked" };

    const status = String(content.progress?.status || "NOT_STARTED").toUpperCase();
    if (status === "COMPLETED") return { icon: CheckCircle2, label: "Terminé", className: "is-completed" };
    if (status === "IN_PROGRESS") return { icon: PlayCircle, label: "En cours", className: "is-progress" };
    return { icon: Circle, label: "Non commencé", className: "is-neutral" };
};

const flattenDisplayRoot = (nodes = []) => {
    let visibleNodes = Array.isArray(nodes) ? nodes : [];
    while (
        visibleNodes.length === 1
        && !visibleNodes[0]?.launchable
        && Array.isArray(visibleNodes[0]?.children)
        && visibleNodes[0].children.length > 0
    ) {
        visibleNodes = visibleNodes[0].children;
    }
    return visibleNodes;
};

const collectExpandableNodeIds = (nodes = [], accumulator = new Set()) => {
    nodes.forEach((node) => {
        const children = node.children || [];
        if (children.length) {
            accumulator.add(String(node.id));
            collectExpandableNodeIds(children, accumulator);
        }
    });
    return accumulator;
};

function ScormSidebarNode({ node, depth, openNodeIds, onToggle, onSelect, currentNodeId }) {
    const children = node.children || [];
    const hasChildren = children.length > 0;
    const isOpen = openNodeIds.has(String(node.id));
    const isActive = String(currentNodeId || "") === String(node.id);
    const isSequencingBlocked = Boolean(node.sequencingBlocked);
    const Icon = node.launchable ? FileText : FolderOpen;

    const handleClick = () => {
        if (isSequencingBlocked) {
            return;
        }
        if (node.launchable) {
            onSelect?.({ nodeId: node.id, scoId: node.scoId || null });
            return;
        }
        if (hasChildren) {
            onToggle(node.id);
        }
    };

    return (
        <div className="sa-scorm-node">
            <button
                type="button"
                className={`sa-scorm-node__button ${isActive ? "sa-scorm-node__button--active" : ""} ${node.launchable ? "is-launchable" : "is-section"} ${isSequencingBlocked ? "is-sequencing-blocked" : ""}`}
                style={{ "--scorm-sidebar-depth": depth }}
                onClick={handleClick}
                disabled={isSequencingBlocked}
                title={node.sequencingBlockReason || undefined}
            >
                <span className="sa-scorm-node__toggle">
                    {hasChildren ? (
                        <ChevronRight size={13} className={isOpen ? "sa-scorm-node__chevron is-open" : "sa-scorm-node__chevron"} />
                    ) : null}
                </span>
                {isSequencingBlocked ? <Lock size={13} /> : <Icon size={13} />}
                <span className="sa-scorm-node__title">{node.title || "Page SCORM"}</span>
            </button>
            {hasChildren && isOpen ? (
                <div className="sa-scorm-node__children">
                    {children.map((child) => (
                        <ScormSidebarNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            openNodeIds={openNodeIds}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            currentNodeId={currentNodeId}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export default function SectionAccordion({ section, sectionNumber, currentContentId, onSelectContent, scormNavigation, onSelectScormNode }) {
    const [open, setOpen] = useState(true);
    const [scormOpenNodeIds, setScormOpenNodeIds] = useState(() => new Set());
    const sectionStatus = String(section.progress?.status || "NOT_STARTED").toUpperCase();

    const completionMeta = useMemo(() => {
        if (sectionStatus === "COMPLETED") return "sa-complete";
        if (sectionStatus === "IN_PROGRESS") return "sa-progress";
        return "sa-neutral";
    }, [sectionStatus]);

    const scormTreeNodes = useMemo(
        () => flattenDisplayRoot(scormNavigation?.tree?.nodes || scormNavigation?.tree || []),
        [scormNavigation?.tree]
    );

    const defaultScormOpenNodeIds = useMemo(() => collectExpandableNodeIds(scormTreeNodes), [scormTreeNodes]);

    useEffect(() => {
        setScormOpenNodeIds(new Set());
    }, [scormNavigation?.contentId, scormNavigation?.packageId]);

    const toggleScormNode = (nodeId) => {
        setScormOpenNodeIds((previous) => {
            const next = new Set(previous.size ? previous : defaultScormOpenNodeIds);
            const key = String(nodeId);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const effectiveOpenNodeIds = scormOpenNodeIds.size ? scormOpenNodeIds : defaultScormOpenNodeIds;

    return (
        <div className={`sa-section ${open ? "is-open" : ""}`}>
            <button className="sa-trigger" onClick={() => setOpen((value) => !value)}>
                <div className="sa-trigger-left">
                    <h3 className="sa-section-title">
                        Section {sectionNumber} : {section.title}
                    </h3>
                    <div className="sa-section-progress">
                        {section.progress?.completedContentsCount || 0} / {section.progress?.totalContentsCount || 0} | {section.progress?.progressPercent || 0}% terminé
                    </div>
                </div>
                <div className="sa-trigger-right">
                    <ChevronDown size={18} className={open ? "sa-icon sa-icon--open" : "sa-icon"} />
                </div>
            </button>

            {open ? (
                <div className="sa-content-list">
                    {(section.contents || []).map((content) => {
                        const statusMeta = getContentStatusMeta(content);
                        const StatusIcon = statusMeta.icon;
                        const isActive = String(currentContentId) === String(content.id);
                        const showScormTree = isActive
                            && String(content.contentType || "").toUpperCase() === "SCORM"
                            && String(scormNavigation?.contentId || "") === String(content.id)
                            && scormTreeNodes.length > 0;
                        
                        return (
                            <React.Fragment key={content.id}>
                                <button
                                    className={`sa-content-item ${isActive ? "is-active" : ""} ${content.locked ? "is-locked" : ""}`}
                                    onClick={() => !content.locked && onSelectContent(content)}
                                    disabled={!!content.locked}
                                >
                                    <div className="sa-content-item-left">
                                        <div className={`sa-status-icon ${statusMeta.className}`}>
                                            <StatusIcon size={16} />
                                        </div>
                                    </div>
                                    <div className="sa-content-item-body">
                                        <div className="sa-content-title">{content.title}</div>
                                        <div className="sa-content-meta">
                                            {content.contentType === "VIDEO" && <PlayCircle size={12} />}
                                            {content.contentType === "PDF" && <FileText size={12} />}
                                            <span>{content.contentType}</span>
                                        </div>
                                    </div>
                                    {content.locked && (
                                        <div className="sa-content-item-right">
                                            <Lock size={14} />
                                        </div>
                                    )}
                                </button>
                                {showScormTree ? (
                                    <div className="sa-scorm-tree" aria-label="Plan interne SCORM">
                                        {scormTreeNodes.map((node) => (
                                            <ScormSidebarNode
                                                key={node.id}
                                                node={node}
                                                depth={0}
                                                openNodeIds={effectiveOpenNodeIds}
                                                onToggle={toggleScormNode}
                                                onSelect={onSelectScormNode}
                                                currentNodeId={scormNavigation?.currentNodeId}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
